import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchHealth } from '../services/health-service';

const POLL_MS = 60_000;

export function useBackendHealth({ refreshTrigger } = {}) {
  const [health, setHealth] = useState({
    status: 'unknown',
    components: {},
    timestamp: null,
    fetchedAt: null,
    error: null,
  });
  const mountedRef = useRef(true);
  const initialTriggerRef = useRef(true);

  const fetchNow = useCallback(async (signal) => {
    try {
      const data = await fetchHealth({ signal });
      if (!mountedRef.current) return;
      setHealth({
        status: data.status || 'unknown',
        components: data.components || {},
        timestamp: data.timestamp || null,
        fetchedAt: Date.now(),
        error: null,
      });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (!mountedRef.current) return;
      setHealth((prev) => ({
        ...prev,
        status: 'down',
        fetchedAt: Date.now(),
        error: err?.message || 'unknown',
      }));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const ac = new AbortController();
    fetchNow(ac.signal);
    const id = setInterval(() => fetchNow(ac.signal), POLL_MS);
    return () => {
      mountedRef.current = false;
      ac.abort();
      clearInterval(id);
    };
  }, [fetchNow]);

  useEffect(() => {
    if (refreshTrigger === undefined) return undefined;
    if (initialTriggerRef.current) {
      initialTriggerRef.current = false;
      return undefined;
    }
    const ac = new AbortController();
    fetchNow(ac.signal);
    return () => ac.abort();
  }, [refreshTrigger, fetchNow]);

  return health;
}
